// A job's photos full size (CCP Cam parity, the office's audit of the work): one at a time over the
// screen, zoomed in to look closely and moved around by scrolling, the previous and next with the
// buttons or the arrow keys, each with its note, who took it and when; a marked-up one shows its
// markup with the original one tap away; a video plays. A picture shows its display copy, with the
// original one tap away; a HEIC whose copy isn't made yet says so, and a file that can't be shown in
// place (a camera RAW photo, an AVI) is a link to it. Escape, or Close, goes back to the grid.

import { useEffect, useRef, useState } from 'react';
import {
  CATEGORY_WORD,
  displayOf,
  otherJobText,
  tileOf,
  viewerSrc,
  viewerWords,
  type Display,
} from './photo-display-model.ts';
import './photo-viewer.css';

export interface ViewerPhoto {
  id: string;
  kind: string;
  category: string;
  caption: string | null;
  /** What to show: the marked-up picture when there is one, else its display copy once made. */
  url: string | null;
  originalUrl: string | null;
  annotated: boolean;
  contentType?: string;
  status?: string;
  display?: Display;
  addedBy: string | null;
  capturedAt: string | null;
  /** At the customer's address on another customer's job (history fix 2): said by its job number. */
  otherCustomer?: boolean;
  jobNumber?: string;
}

const ZOOMS = [1, 1.5, 2, 3] as const;

export function PhotoViewer({
  photos,
  start,
  timeZone,
  onClose,
  onCaption,
}: {
  photos: readonly ViewerPhoto[];
  start: number;
  timeZone: string;
  onClose: () => void;
  /** Write its note (attachments.setCaption), for those who may; resolves once saved. */
  onCaption?: (id: string, caption: string | null) => Promise<void>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [i, setI] = useState(Math.min(Math.max(0, start), photos.length - 1));
  const [zoom, setZoom] = useState(0);
  const [original, setOriginal] = useState(false);
  // the note being written here, what saving it said, and the notes saved while open
  const [note, setNote] = useState<string | null>(null);
  const [saying, setSaying] = useState<string | null>(null);
  const [saved, setSaved] = useState<Record<string, string | null>>({});
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  // a new photo starts whole, and marked up when it is
  useEffect(() => {
    setZoom(0);
    setOriginal(false);
    setNote(null);
    setSaying(null);
  }, [i]);
  const p = photos[i];
  const go = (d: number) => setI((x) => Math.min(Math.max(0, x + d), photos.length - 1));
  // the keys work wherever focus is while it's open (a button that just went disabled lets it go)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setI((x) => Math.min(x + 1, photos.length - 1));
      else if (e.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1));
      else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(ZOOMS.length - 1, z + 1));
      else if (e.key === '-') setZoom((z) => Math.max(0, z - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [photos.length]);
  if (!p) return null;
  const shown = { ...p, contentType: p.contentType ?? '', status: p.status ?? 'verified' };
  const d = displayOf(shown);
  const src = viewerSrc(shown, original);
  const caption = p.id in saved ? (saved[p.id] ?? null) : p.caption;
  const saveNote = async () => {
    if (!onCaption || note === null) return;
    const text = note.trim() || null;
    setSaying(null);
    try {
      await onCaption(p.id, text);
      setSaved((x) => ({ ...x, [p.id]: text }));
      setNote(null);
      setSaying(text ? 'Note saved.' : 'Note taken off.');
    } catch (e) {
      setSaying(e instanceof Error ? e.message : 'The note wasn’t saved. Try again.');
    }
  };
  // its note, else a made-up name ("Before photo"): a caption that is only the file's name is no note
  // (P-0161), and that name shows only as the original file's, under the heading
  const { title: what, note: noted, file } = viewerWords(shown, caption);
  const when = p.capturedAt
    ? new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(p.capturedAt),
      )
    : null;
  return (
    <dialog
      ref={ref}
      className="photo-viewer"
      aria-label={`${what}, ${String(i + 1)} of ${String(photos.length)}`}
      onClose={onClose}
    >
      <div className="pv-bar">
        <span className="small">
          {String(i + 1)} of {String(photos.length)}
        </span>
        <div className="row">
          {d === 'image' && (
            <>
              <button className="btn soft" disabled={zoom === 0} onClick={() => setZoom((z) => z - 1)}>
                Zoom out
              </button>
              <button
                className="btn soft"
                disabled={zoom === ZOOMS.length - 1}
                onClick={() => setZoom((z) => z + 1)}
              >
                Zoom in
              </button>
            </>
          )}
          <form method="dialog">
            <button className="btn soft" type="submit">
              Close
            </button>
          </form>
        </div>
      </div>
      {/* zoomed in, the picture is wider than its frame: scroll (or drag a trackpad) to move around it */}
      <div className="pv-frame" tabIndex={0} aria-label="The photo; scroll to move around it when zoomed in">
        {d === 'preparing' ? (
          <p className="muted">Preparing photo…</p>
        ) : !src ? (
          <p className="muted">Not uploaded yet.</p>
        ) : d === 'video' ? (
          <video src={src} controls preload="metadata" className="pv-media pv-fit" />
        ) : d !== 'image' ? (
          <p>
            <a href={src} target="_blank" rel="noreferrer" title={file ?? undefined}>
              {(() => {
                const t = tileOf(shown);
                return t.t === 'file' ? t.words : 'Open the file';
              })()}
            </a>
          </p>
        ) : (
          <img
            src={src}
            alt={`${what}${p.annotated && !original ? ', marked up' : ''}`}
            // whole and fitted at first; zoomed in, wider than the frame, which then scrolls
            className={zoom === 0 ? 'pv-media pv-fit' : 'pv-media'}
            {...(zoom === 0 ? {} : { style: { width: `${String((ZOOMS[zoom] ?? 1) * 100)}%` } })}
            onDoubleClick={() => setZoom((z) => (z === 0 ? 2 : 0))}
          />
        )}
      </div>
      <div className="pv-foot">
        <div>
          <b>{what}</b>
          <p className="small muted">
            {[
              noted ? (CATEGORY_WORD[p.category] ?? p.category) : null,
              p.addedBy && `Taken by ${p.addedBy}`,
              when,
              otherJobText(p),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {file && <p className="small muted">Original file: {file}</p>}
          {note !== null ? (
            <div className="pv-note">
              <label className="small">
                The note
                <textarea value={note} maxLength={500} rows={2} onChange={(e) => setNote(e.target.value)} />
              </label>
              <div className="row">
                <button className="btn primary" onClick={() => void saveNote()}>
                  Save the note
                </button>
                <button className="btn soft" onClick={() => setNote(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : onCaption ? (
            <button className="btn quiet" onClick={() => setNote(noted ?? '')}>
              {noted ? 'Edit the note' : 'Add a note'}
            </button>
          ) : null}
          {saying && (
            <p className="small" role="status">
              {saying}
            </p>
          )}
        </div>
        <div className="row">
          {p.annotated && p.originalUrl && (
            <button className="btn soft" onClick={() => setOriginal((o) => !o)}>
              {original ? 'See the markup' : 'See the original'}
            </button>
          )}
          {/* shown as its display copy: the file itself, full size, in its own tab */}
          {!p.annotated && p.originalUrl && d === 'image' && (
            <a
              className="btn soft"
              href={p.originalUrl}
              target="_blank"
              rel="noreferrer"
              title={file ?? undefined}
            >
              Open the original
            </a>
          )}
          <button className="btn" disabled={i === 0} onClick={() => go(-1)}>
            Previous
          </button>
          <button className="btn" disabled={i === photos.length - 1} onClick={() => go(1)}>
            Next
          </button>
        </div>
      </div>
    </dialog>
  );
}
